import React, { FC, ReactNode, useCallback, useEffect, useLayoutEffect, useState } from "react";
import { connect } from "react-redux";

import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cn from "classnames";
import Highcharts from "highcharts";
import { useIntl } from "react-intl";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import localSettings from "@app/config/LocalSettings";
import CardType from "@app/enum/dashboard/CardType";
import GoalType from "@app/enum/dashboard/GoalType";
import TargetType from "@app/enum/dashboard/TargetType";
import getGoalAmount from "@app/scene/dashboard/utils/getGoalAmount";
import useCard from "@app/scene/dashboard/utils/useCard";
import { isManager } from "@app/store/iam/util";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { GoalConfiguration } from "@app/types/dashboard/CardConfiguration";
import Scope from "@app/types/dashboard/Scope";

import styles from "./GoalProgress.module.scss";

export interface GoalProgressProps {
  className?: string;
  currencyType?: boolean;
  goalConfiguration?: GoalConfiguration;
  label: ReactNode;
  onMouseEnter?: (goal?: number) => void;
  onMouseLeave?: (goal: undefined) => void;
  scope: Scope;
  type: CardType;
  value?: number;
}

interface Props extends GoalProgressProps {
  teams: Team[];
  users: User[];
}

const GoalProgress: FC<Props> = ({
  className,
  currencyType,
  goalConfiguration,
  label,
  onMouseEnter,
  onMouseLeave,
  scope,
  teams,
  type,
  users,
  value = 0,
}) => {
  const intl = useIntl();
  const { getGoalType } = useCard(type);
  const { format, goalType } = getGoalType({ configuration: goalConfiguration });
  const goal = getGoalAmount(goalConfiguration, scope, teams) ?? 0;
  const goalMet = value >= goal;

  const [displayedProgress, setDisplayedProgress] = useState("0%");
  useEffect(() => {
    // this timeout makes animated progress bar: setting to 0 and then to an actual progress
    setTimeout(() => {
      setDisplayedProgress(`${goalMet ? 100 : goal ? (value / goal) * 100 : 0}%`);
    }, 0);
  }, [goal, value, goalMet]);

  const handleMouseEnter = useCallback(() => onMouseEnter?.(goal), [goal, onMouseEnter]);
  const handleMouseLeave = useCallback(() => onMouseLeave?.(undefined), [onMouseLeave]);

  const user = users.find(({ id }) => id === scope.userId);
  const shouldHideBecauseOfManager =
    user && goalConfiguration?.type === TargetType.MEMBERS && isManager(user);
  useLayoutEffect(
    () => () => {
      // charts don't handle height changes very well, hiding/showing a GoalProgress
      // does change their height. So we're force-reflowing them to fit better. Wish we could only
      // reflow the ones we need...
      Highcharts.charts.forEach((chart) => chart?.reflow());
    },
    [shouldHideBecauseOfManager]
  );

  if (goalType === GoalType.NO_GOAL) {
    return null;
  }

  if (shouldHideBecauseOfManager) {
    return null;
  }

  return (
    <div
      className={cn(
        styles.container,
        {
          [styles.goalMet]: goalMet,
          [styles.maxProgress]: goalMet && goalType === GoalType.MAX_GOAL,
        },
        className
      )}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.progressBar} style={{ width: displayedProgress }} />
      <div className={styles.label}>
        {goalMet
          ? intl.formatMessage(
              {
                id: "dashboard.cards.goalProgress.met",
                defaultMessage:
                  "{goalType, select, 2 {Max} other {Goal}}: {goal}{showHour, select, true {h} other {}} {icon}",
                description: "Text in goal progress when goal is met on Dashboard cards",
              },
              {
                goal: intl.formatNumber(
                  goal,
                  currencyType
                    ? {
                        currency: localSettings.getCurrencyCode(),
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                        style: "currency",
                      }
                    : { maximumFractionDigits: 2 }
                ),
                goalType,
                icon:
                  goalType === GoalType.MIN_GOAL ? (
                    <FontAwesomeIcon className={styles.checkIcon} icon={faCheckCircle} />
                  ) : null,
                showHour: format === "time",
              }
            )
          : intl.formatMessage(
              {
                id: "dashboard.cards.goalProgress",
                defaultMessage: `{showLabel, select, true {{label}: } other {}}{value}/{goal}{showHour, select, true {h} other {}}`,
                description:
                  "Text in goal progress showing current goal progress on Dashboard cards",
              },
              {
                goal: intl.formatNumber(
                  goal,
                  currencyType
                    ? {
                        currency: localSettings.getCurrencyCode(),
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                        style: "currency",
                      }
                    : { maximumFractionDigits: 2 }
                ),
                label,
                showHour: format === "time",
                showLabel: !!label,
                value: intl.formatNumber(
                  value,
                  currencyType
                    ? {
                        currency: localSettings.getCurrencyCode(),
                        maximumFractionDigits: 2,
                        minimumFractionDigits: 2,
                        style: "currency",
                      }
                    : { maximumFractionDigits: 2 }
                ),
              }
            )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  teams: getTeams(state),
  users: getUsers(state),
});

export default connect(mapStateToProps)(GoalProgress);
